import React, { useRef, useState, useEffect } from 'react';
import { Tree } from 'primereact/tree';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Toast } from 'primereact/toast';
import { FiEdit3, FiPlay } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import studentApi from './StudentApi';

const ExamTopicTree = ({ studentId, onSelectionChange, onPracticeClick, onExamsLoaded }) => {
  const [treeData, setTreeData] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [selection, setSelection] = useState({ examId: '', subjectId: '', topicId: '' });
  const toastRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const examsRes = await studentApi.get('/exams-with-subjects-topics');
        const allExams = examsRes.data?.data || [];
        console.log('Exams API response:', allExams);

       const vouchersRes = await studentApi.get(`/student-vouchers/${studentId}`);
       console.log("Student ID used for vouchers API:", studentId);
        const vouchers = vouchersRes.data?.data || [];
        console.log("Vouchers full response:", vouchersRes);
        console.log('Vouchers API response:', vouchers);

        // Extract active vouchers
        const activeVouchers = vouchers.filter(v => v.status === 'active' && v.is_delete === 0);
        console.log('Active vouchers:', activeVouchers);

        // Filter exams that match active vouchers
        const allowedExamIds = activeVouchers.map(v => String(v.exam_id));
        const filteredExams = allExams.filter(exam => allowedExamIds.includes(String(exam.id)));
        console.log('Filtered Exams:', filteredExams);

        // Build tree data only for allowed exams
        const tree = buildTreeDataWithVouchers(allExams, activeVouchers);
        setTreeData(tree);

        // Notify parent about number of exams
        if (onExamsLoaded) onExamsLoaded(filteredExams.length);

      } catch (err) {
        console.error('Error fetching exams or vouchers:', err);
        if (onExamsLoaded) onExamsLoaded(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId, onExamsLoaded]);

  const buildTreeDataWithVouchers = (allExams, activeVouchers) => {
  return allExams.map(exam => {
    // vouchers for this exam
    const examVouchers = activeVouchers.filter(v => v.exam_id === exam.id);
    if (!examVouchers.length) return null;

    const subjects = (exam.subjects || []).map(subject => {
      const subjectVouchers = examVouchers.filter(v => !v.subject_id || v.subject_id === subject.id);
      if (!subjectVouchers.length) return null;

      const topics = (subject.topics || []).map(topic => {
        const topicVouchers = subjectVouchers.filter(v => !v.topic_id || v.topic_id === topic.id);
        if (!topicVouchers.length) return null;
        return { key: `topic-${exam.id}-${subject.id}-${topic.id}`, label: topic.title };
      }).filter(Boolean);

      if (topics.length || subjectVouchers.some(v => !v.topic_id)) {
        return { key: `subject-${exam.id}-${subject.id}`, label: subject.name, children: topics };
      }
      return null;
    }).filter(Boolean);

    if (subjects.length || examVouchers.some(v => !v.subject_id)) {
      return { key: `exam-${exam.id}`, label: exam.title, children: subjects };
    }

    return null;
  }).filter(Boolean);
};


  const getNamesFromIds = (examId, subjectId, topicId) => {
    const exam = treeData.find(e => e.key === `exam-${examId}`);
    if (!exam) return {};
    let examName = exam.label;
    let subjectName = '';
    let topicName = '';
    if (subjectId) {
      const subject = exam.children?.find(s => s.key === `subject-${examId}-${subjectId}`);
      subjectName = subject?.label || '';
      if (topicId) {
        const topic = subject?.children?.find(t => t.key === `topic-${examId}-${subjectId}-${topicId}`);
        topicName = topic?.label || '';
      }
    }
    return { examName, subjectName, topicName };
  };

  const handleSelection = (e) => {
    const fullKey = e.value;
    if (!fullKey) return;

    const parts = fullKey.split('-');
    let updated = { examId: '', subjectId: '', topicId: '' };

    if (parts[0] === 'exam' && parts.length === 2) updated.examId = parts[1];
    else if (parts[0] === 'subject' && parts.length === 3) {
      updated.examId = parts[1];
      updated.subjectId = parts[2];
    } else if (parts[0] === 'topic' && parts.length === 4) {
      updated.examId = parts[1];
      updated.subjectId = parts[2];
      updated.topicId = parts[3];
    }

    setSelection(updated);
    setSelectedKey(fullKey);

    if (onSelectionChange) onSelectionChange(updated);

    if (parts[0] === 'topic' && toastRef.current) {
      const { examName, subjectName, topicName } = getNamesFromIds(updated.examId, updated.subjectId, updated.topicId);
      toastRef.current.show({
        severity: 'success',
        summary: 'Topic Selected',
        detail: `Exam ${examName} → Subject ${subjectName} → Topic ${topicName}`,
        life: 3000,
      });
    }
  };

  const handleNodeClick = (e) => {
    const key = e.node.key;
    setExpandedKeys(prev => ({ ...prev, [key]: prev[key] ? undefined : true }));
  };

  const extractIdsFromKey = (key) => {
    const parts = key.split('-');
    if (parts[0] === 'exam' && parts.length === 2) return { examId: parts[1] };
    if (parts[0] === 'subject' && parts.length === 3) return { examId: parts[1], subjectId: parts[2] };
    if (parts[0] === 'topic' && parts.length === 4) return { examId: parts[1], subjectId: parts[2], topicId: parts[3] };
    return {};
  };

  const getNodeStyle = (level) => {
    const baseColor = '#0d47a1';
    const shade = { exam: '#64b5f6', subject: '#bbdefb', topic: '#e3f2fd' };
    let style = { padding: '6px 12px', borderRadius: '6px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' };
    if (level === 2) style = { ...style, backgroundColor: shade.exam, color: baseColor, fontWeight: 'bold', fontSize: '1rem' };
    else if (level === 3) style = { ...style, backgroundColor: shade.subject, color: baseColor, fontWeight: 600 };
    else if (level === 4) style = { ...style, backgroundColor: shade.topic, color: baseColor };
    return style;
  };

  const nodeTemplate = (node) => {
    const level = (node?.key?.split('-').length) || 0;
    const iconClass = level === 2 ? 'pi pi-folder' : level === 3 ? 'pi pi-book' : level === 4 ? 'pi pi-file' : 'pi pi-question';

    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{ ...getNodeStyle(level), flexWrap: 'wrap' }}>
        <Box display="flex" alignItems="center" flexShrink={1}>
          <i className={iconClass} style={{ marginRight: 6 }} />
          <Typography variant="body2">{node?.label || ''}</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginLeft: 2, marginTop: { xs: 1, sm: 0 }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          {isMobile ? (
            <>
              <IconButton color="secondary" size="small" onClick={e => { e.stopPropagation(); const { examId, topicId } = extractIdsFromKey(node.key); if (!examId || !topicId) { alert('Select a topic node.'); return; } navigate('/practice', { state: { examId, topicId } }); }}>
                <FiEdit3 />
              </IconButton>
              <IconButton color="primary" size="small" onClick={e => { e.stopPropagation(); const { examId, topicId } = extractIdsFromKey(node.key); if (!examId || !topicId) { alert('Select a topic node.'); return; } navigate('/test', { state: { examId, topicId } }); }}>
                <FiPlay />
              </IconButton>
            </>
          ) : (
            <>
              <Button variant="outlined" color="secondary" size="small" startIcon={<FiEdit3 />} onClick={e => { e.stopPropagation(); const ids = extractIdsFromKey(node.key); if (!ids.examId) return; navigate('/practice', { state: ids }); }} sx={{ borderRadius: '999px', textTransform: 'none', fontSize: '0.75rem' }}>Practice</Button>

              <Button variant="outlined" color="primary" size="small" startIcon={<FiPlay />} onClick={e => { e.stopPropagation(); if (onPracticeClick) { const ids = extractIdsFromKey(node.key); onPracticeClick(ids); } }}>
                Test
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '700px', mx: 'auto', px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 } }}>
      <Toast ref={toastRef} position="bottom-right" />
      <Typography variant="subtitle1" gutterBottom>📚 Select Class → Subject → Topic</Typography>
      <Box mt={1}>
        <Tree
          value={treeData}
          selectionMode="single"
          selectionKeys={selectedKey}
          onSelectionChange={handleSelection}
          expandedKeys={expandedKeys}
          onToggle={e => setExpandedKeys(e.value)}
          onNodeClick={handleNodeClick}
          nodeTemplate={nodeTemplate}
          className="clean-tree"
          loading={loading}
        />
      </Box>
      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Selected: {selection.examId}{selection.subjectId && ` → ${selection.subjectId}`}{selection.topicId && ` → ${selection.topicId}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExamTopicTree;
